import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { clientsApi } from '@/services/clients'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import type {
  ClientAssetListQuery,
  ClientAssetRequest,
  ClientContactPayload,
  ClientListQuery,
  CreateClientRequest,
  EditClientRequest,
  ListQuery,
} from '@/types'

export function useClientsList(q: ClientListQuery) {
  return useQuery({
    queryKey: ['clients', 'list', q],
    queryFn: () => clientsApi.list(q),
  })
}

export function useClientsSummary(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['clients', 'summary'],
    queryFn: () => clientsApi.summary(),
    enabled: opts?.enabled ?? true,
  })
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ['clients', 'detail', id],
    queryFn: () => clientsApi.get(id as string),
    enabled: !!id,
  })
}

export function useCreateClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateClientRequest) => clientsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients', 'list'] })
      qc.invalidateQueries({ queryKey: ['clients', 'summary'] })
      invalidatePortalMetricQueries(qc)
    },
  })
}

export function useUpdateClient(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EditClientRequest) => clientsApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients', 'list'] })
      qc.invalidateQueries({ queryKey: ['clients', 'detail', id] })
      qc.invalidateQueries({ queryKey: ['clients', 'summary'] })
      invalidatePortalMetricQueries(qc)
    },
  })
}

export function useDeleteClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => clientsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients', 'list'] })
      qc.invalidateQueries({ queryKey: ['clients', 'summary'] })
      invalidatePortalMetricQueries(qc)
    },
  })
}

export function useActivateClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => clientsApi.activate(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['clients', 'list'] })
      qc.invalidateQueries({ queryKey: ['clients', 'detail', id] })
      qc.invalidateQueries({ queryKey: ['clients', 'summary'] })
      invalidatePortalMetricQueries(qc)
    },
  })
}

export function useDeactivateClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => clientsApi.deactivate(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['clients', 'list'] })
      qc.invalidateQueries({ queryKey: ['clients', 'detail', id] })
      qc.invalidateQueries({ queryKey: ['clients', 'summary'] })
      invalidatePortalMetricQueries(qc)
    },
  })
}

export function useUpdateClientNotes(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (notes: string) => clientsApi.updateNotes(id, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients', 'detail', id] })
      qc.invalidateQueries({ queryKey: ['clients', id, 'activity'] })
    },
  })
}

// Keep hover-prefetched client tab data fresh long enough for the subsequent
// tab selection to reuse it without immediately issuing the same request.
const CLIENT_TAB_STALE_TIME = 30_000

// --- detail sub-tab queries ---
export function clientQuotesQueryOptions(id: string, q: ListQuery) {
  return queryOptions({
    queryKey: ['clients', id, 'quotes', q],
    queryFn: () => clientsApi.quotes(id, q),
    staleTime: CLIENT_TAB_STALE_TIME,
  })
}

export function useClientQuotes(id: string, q: ListQuery) {
  return useQuery(clientQuotesQueryOptions(id, q))
}

export function clientContractsQueryOptions(id: string, q: ListQuery) {
  return queryOptions({
    queryKey: ['clients', id, 'contracts', q],
    queryFn: () => clientsApi.contracts(id, q),
    staleTime: CLIENT_TAB_STALE_TIME,
  })
}

export function useClientContracts(id: string, q: ListQuery) {
  return useQuery(clientContractsQueryOptions(id, q))
}

export function clientJobsQueryOptions(id: string, q: ListQuery) {
  return queryOptions({
    queryKey: ['clients', id, 'jobs', q],
    queryFn: () => clientsApi.jobs(id, q),
    staleTime: CLIENT_TAB_STALE_TIME,
  })
}

export function useClientJobs(id: string, q: ListQuery) {
  return useQuery(clientJobsQueryOptions(id, q))
}

export function clientReportsQueryOptions(id: string, q: ListQuery) {
  return queryOptions({
    queryKey: ['clients', id, 'reports', q],
    queryFn: () => clientsApi.reports(id, q),
    staleTime: CLIENT_TAB_STALE_TIME,
  })
}

export function useClientReports(id: string, q: ListQuery) {
  return useQuery(clientReportsQueryOptions(id, q))
}

export function clientAssetsQueryOptions(id: string, q: ClientAssetListQuery) {
  return queryOptions({
    queryKey: ['clients', id, 'assets', q],
    queryFn: () => clientsApi.assets(id, q),
    staleTime: CLIENT_TAB_STALE_TIME,
  })
}

export function useClientAssets(id: string, q: ClientAssetListQuery) {
  return useQuery(clientAssetsQueryOptions(id, q))
}

export function useCreateClientAsset(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ClientAssetRequest) =>
      clientsApi.createAsset(clientId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients', clientId, 'assets'] })
      qc.invalidateQueries({ queryKey: ['clients', 'detail', clientId] })
    },
  })
}

export function useUpdateClientAsset(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      assetId,
      body,
    }: {
      assetId: string
      body: ClientAssetRequest
    }) => clientsApi.updateAsset(clientId, assetId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients', clientId, 'assets'] })
      qc.invalidateQueries({ queryKey: ['clients', 'detail', clientId] })
    },
  })
}

export function useDeleteClientAsset(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (assetId: string) => clientsApi.deleteAsset(clientId, assetId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients', clientId, 'assets'] })
      qc.invalidateQueries({ queryKey: ['clients', 'detail', clientId] })
    },
  })
}

export function clientInvoicesQueryOptions(
  id: string,
  q: ListQuery & { status?: string }
) {
  return queryOptions({
    queryKey: ['clients', id, 'invoices', q],
    queryFn: () => clientsApi.invoices(id, q),
    staleTime: CLIENT_TAB_STALE_TIME,
  })
}

export function useClientInvoices(
  id: string,
  q: ListQuery & { status?: string }
) {
  return useQuery(clientInvoicesQueryOptions(id, q))
}

export function clientPaymentsQueryOptions(
  id: string,
  q: ListQuery & { status?: string }
) {
  return queryOptions({
    queryKey: ['clients', id, 'payments', q],
    queryFn: () => clientsApi.payments(id, q),
    staleTime: CLIENT_TAB_STALE_TIME,
  })
}

export function useClientPayments(
  id: string,
  q: ListQuery & { status?: string }
) {
  return useQuery(clientPaymentsQueryOptions(id, q))
}

export function clientDocumentsQueryOptions(id: string, q: ListQuery) {
  return queryOptions({
    queryKey: ['clients', id, 'documents', q],
    queryFn: () => clientsApi.documents(id, q),
    staleTime: CLIENT_TAB_STALE_TIME,
  })
}

export function useClientDocuments(id: string, q: ListQuery) {
  return useQuery(clientDocumentsQueryOptions(id, q))
}

export function useCreateClientContact(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ClientContactPayload) =>
      clientsApi.createContact(clientId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients', 'detail', clientId] })
      qc.invalidateQueries({ queryKey: ['clients', 'list'] })
      qc.invalidateQueries({ queryKey: ['clients', clientId, 'activity'] })
    },
  })
}

export function useUpdateClientContact(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      contactId,
      body,
    }: {
      contactId: string
      body: ClientContactPayload
    }) => clientsApi.updateContact(clientId, contactId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients', 'detail', clientId] })
      qc.invalidateQueries({ queryKey: ['clients', 'list'] })
      qc.invalidateQueries({ queryKey: ['clients', clientId, 'activity'] })
    },
  })
}

export function useDeleteClientContact(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (contactId: string) =>
      clientsApi.deleteContact(clientId, contactId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients', 'detail', clientId] })
      qc.invalidateQueries({ queryKey: ['clients', 'list'] })
      qc.invalidateQueries({ queryKey: ['clients', clientId, 'activity'] })
    },
  })
}

export function useClientActivity(id: string, q: ListQuery) {
  return useQuery({
    queryKey: ['clients', id, 'activity', q],
    queryFn: () => clientsApi.activity(id, q),
  })
}
